/**
 * Stale code is fixed by a reload, once. A guard in sessionStorage remembers
 * the last automatic reload, so a chunk that is really gone (or a dev module
 * still broken after the reload) shows "A newer version of the app is
 * available" with a Reload button instead of reloading in a loop.
 *
 * Shared by the error boundaries and `main.tsx`'s `vite:preloadError` handler.
 */

const KEY = "scandi:stale-reload";
/** Within this long of the last automatic reload, the next stale error is shown instead. */
const GUARD_MS = 60_000;
/**
 * How long after asking for a reload the page counts as reloading. A reload
 * can be held up (a page with unsaved settings asks first), so after this the
 * screens stop waiting for it and show their Reload button.
 */
const RELOAD_GRACE_MS = 5_000;

let reloadAskedAt = 0;

function lastReload(): number {
  try {
    return Number(sessionStorage.getItem(KEY) ?? 0) || 0;
  } catch {
    // Storage blocked: behave as if a reload was just made, so nothing loops.
    return Date.now();
  }
}

function startReload(): void {
  reloadAskedAt = Date.now();
  window.location.reload();
}

/** Whether an automatic reload happened within the guard window. */
export function staleReloadRecently(): boolean {
  return Date.now() - lastReload() < GUARD_MS;
}

/** A reload was asked for a moment ago: a screen shows the quiet "Reloading…" rather than an error. */
export function reloadPending(): boolean {
  return Date.now() - reloadAskedAt < RELOAD_GRACE_MS;
}

/** Milliseconds until `reloadPending()` turns false (0 when it already is). */
export function reloadGraceLeft(): number {
  return Math.max(0, RELOAD_GRACE_MS - (Date.now() - reloadAskedAt));
}

/**
 * Reload the page to fetch the current build, unless one was done in the last
 * minute. Returns true when it reloads (the caller shows nothing meanwhile).
 */
export function reloadOnceForStaleCode(): boolean {
  if (reloadPending()) return true;
  if (staleReloadRecently()) return false;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  startReload();
  return true;
}

/**
 * A person pressed Reload. The guard is left as it is: if the code is still
 * stale after this reload, the screen comes back at once rather than after a
 * second, automatic reload.
 */
export function reloadNow(): void {
  startReload();
}
